import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import {
    faCaretDown,
    faCaretUp,
    faFileExport,
    faFileImport,
    faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { generateExports } from 'api/contacts/exports';
import { deleteFund as deleteFundApi, fetchFunds, reorderFunds } from 'api/funds';
import arrayMove from 'array-move';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Import from 'components/imports/Import';
import * as DownloadImportTemplateModal from 'components/pages/contacts/components/DownloadImportTemplateModal';
import * as Table from 'components/table';
import { EmptyData } from 'components/util';
import { FUNDS_PARAMS } from 'constants/params';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Fund } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<Fund>();

const FundsSettings = () => {
    const history = useHistory();
    const { url: matchUrl } = useRouteMatch();
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const [isReorderingFunds, setIsReorderingFunds] = useState(false);
    const { importFunds } = useFlags();

    const { data: funds, isRefetching } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundsWithFilters(accountId, FUNDS_PARAMS),
        queryFn: () => fetchFunds(accountId, FUNDS_PARAMS),
    });

    useEffect(() => {
        if (!isRefetching) setIsReorderingFunds(false);
    }, [isRefetching]);

    const { isPending: isGeneratingReport, mutateAsync: onSubmitExport } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                scope: 'all',
                object: 'account',
                resource: 'funds',
                report_type: 'csv',
                object_id: accountId,
            }),
    });

    const { mutate: updateFundOrder } = useMutation<unknown, GivebutterAxiosError, number[]>({
        mutationFn: (order: number[]) => reorderFunds(accountId, { order }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.funds(accountId) });
        },
        onError: e => {
            error(e?.response?.data.message || 'Something went wrong. Please try again.');
            setIsReorderingFunds(false);
        },
    });

    const { isPending: isDeletingFund, mutate: deleteFund } = useMutation({
        mutationFn: (fundId: number) => deleteFundApi(accountId, fundId),
        onSuccess: () => {
            success('Fund deleted successfully.');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.funds(accountId),
                type: 'all',
            });
        },
        onError: () => {
            error('Could not delete fund. Please try again.');
        },
    });

    const columns = useMemo(() => {
        const onClickMove = (id: number, direction: 'up' | 'down') => {
            setIsReorderingFunds(true);
            const fundOrder = funds.data.map(fund => fund.id);
            const currentIndex = fundOrder.findIndex(value => value === id);
            const lastIndex = fundOrder.length - 1;
            const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
            const newOrder = arrayMove(
                fundOrder,
                currentIndex,
                newIndex > lastIndex ? 0 : newIndex,
            );
            updateFundOrder(newOrder);
        };

        return [
            columnHelper.accessor('name', {
                header: 'Name',
                enableSorting: false,
                cell: info => {
                    const fund = info.row.original;
                    return (
                        <div className="flex items-center gap-2">
                            {funds.data.length > 1 ? (
                                <div className="flex flex-col justify-center">
                                    <Button
                                        className="!h-[1rem]"
                                        size="sm"
                                        variant="transparent"
                                        shape={'square'}
                                        onClick={() => onClickMove(fund.id, 'up')}
                                        disabled={isReorderingFunds}
                                        aria-label="Move up"
                                    >
                                        <FontAwesomeIcon
                                            icon={faCaretUp}
                                            className="h-full w-full text-gray-300 hover:text-gray-500 focus:text-gray-500 focus-visible:text-gray-500"
                                        />
                                    </Button>

                                    <Button
                                        className="!h-[1rem]"
                                        size="sm"
                                        variant="transparent"
                                        onClick={() => onClickMove(fund.id, 'down')}
                                        disabled={isReorderingFunds}
                                        shape={'square'}
                                        aria-label="Move down"
                                    >
                                        <FontAwesomeIcon
                                            icon={faCaretDown}
                                            className="h-full w-full text-gray-300 hover:text-gray-500 focus:text-gray-500 focus-visible:text-gray-500"
                                        />
                                    </Button>
                                </div>
                            ) : null}

                            <div className="max-w-[150px] truncate md:max-w-sm">{fund.name}</div>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('code', {
                header: 'Code',
                enableSorting: false,
                cell: info => {
                    const fund = info.row.original;

                    if (fund.code) {
                        return <Badge>{fund.code}</Badge>;
                    }

                    return <EmptyData />;
                },
            }),
            columnHelper.accessor('raised', {
                header: 'Raised',
                enableSorting: false,
                cell: info => {
                    const fund = info.row.original;
                    return (
                        <div className="flex flex-col justify-start">
                            <p>${maskDecimal(fund.raised)}</p>
                            <p className="whitespace-nowrap text-gray-500">
                                {`${fund.supporters} supporter${fund.supporters !== 1 ? 's' : ''}`}
                            </p>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const fund = info.row.original;
                    return (
                        <DropdownMenu.Root>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content>
                                <DropdownMenu.LinkItem to={`${matchUrl}/${fund.id}/edit`}>
                                    Edit fund
                                </DropdownMenu.LinkItem>
                                <AlertDialog.Root type="error">
                                    <AlertDialog.Trigger asChild>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className="focus-visible:before:border-0"
                                        >
                                            Delete fund
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete fund {fund.name}?
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You won't be able to undo this action. Are you
                                                    sure?
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => {
                                                        deleteFund(fund.id);
                                                    }}
                                                >
                                                    Delete fund
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ];
    }, [funds, isReorderingFunds, matchUrl, updateFundOrder, deleteFund]);

    const table = useReactTable({
        data: funds.data,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        pageCount: 1,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <Box className="overflow-hidden">
            <div className="flex flex-col">
                <div className="grid grid-cols-12 gap-2 px-6 py-4">
                    <div className="col-span-full flex flex-col md:col-span-8">
                        <p className="text-2xl font-medium">Funds</p>
                        <p className="text-sm">
                            Allow supporters to designate their contribution to a specific fund.
                        </p>
                    </div>
                    <div className="col-span-full flex items-end justify-start gap-2 md:col-span-4 md:justify-end">
                        <ExportDataDialog.Root label="Funds" onSubmit={onSubmitExport}>
                            <ExportDataDialog.Trigger asChild={true}>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    color="neutral"
                                    disabled={!funds.data.length}
                                >
                                    <FontAwesomeIcon icon={faFileExport} />
                                    Export
                                </Button>
                            </ExportDataDialog.Trigger>
                        </ExportDataDialog.Root>

                        {importFunds && (
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button variant="outline" color="neutral" size="sm">
                                        <FontAwesomeIcon icon={faFileImport} />
                                        Import
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    <Suspense
                                        fallback={
                                            <DropdownMenu.Item
                                                disabled={true}
                                                className="grid grid-cols-[16px_1fr] items-center gap-2"
                                            >
                                                <FontAwesomeIcon icon={faFileImport} />
                                                Funds import
                                            </DropdownMenu.Item>
                                        }
                                    >
                                        <Import.Root strategy="funds">
                                            <Import.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onClick={e => e.preventDefault()}
                                                    className="grid grid-cols-[16px_1fr] items-center gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faFileImport} />
                                                    Funds import
                                                </DropdownMenu.Item>
                                            </Import.Trigger>
                                            <Import.Portal>
                                                <Import.Space />
                                            </Import.Portal>
                                        </Import.Root>
                                    </Suspense>
                                    <DownloadImportTemplateModal.Root type={'funds'}>
                                        <DownloadImportTemplateModal.Trigger asChild={true}>
                                            <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                                Download import template
                                            </DropdownMenu.Item>
                                        </DownloadImportTemplateModal.Trigger>
                                    </DownloadImportTemplateModal.Root>
                                    <DropdownMenu.Item
                                        onClick={() =>
                                            history.push(`/accounts/${accountId}/settings/imports`)
                                        }
                                    >
                                        View import history
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        )}

                        <Link to={`${matchUrl}/new`}>
                            <Button variant="solid" size="sm">
                                <FontAwesomeIcon icon={faPlus} size="sm" /> Add new
                            </Button>
                        </Link>
                    </div>
                </div>
                <Separator />
                <div className="relative overflow-x-auto">
                    <Table.Root
                        isLoading={isGeneratingReport || isReorderingFunds || isDeletingFund}
                        variant="bordered"
                        table={table}
                    >
                        <div className="flex flex-col gap-y-4">
                            <Table.Content>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'secondary'}>
                                <Table.EmptyIcon />
                                <Table.EmptyTitle>No funds found</Table.EmptyTitle>
                            </Table.Empty>
                        </div>
                    </Table.Root>
                </div>
            </div>
        </Box>
    );
};

export { FundsSettings };
